import React, { useContext, useRef, useState } from 'react';
import { EditTwoTone, LeftOutlined, RightOutlined } from '@ant-design/icons';
import { Button, Card, Carousel } from 'antd';
import type { CarouselRef } from 'antd/lib/carousel';

import BuildingContext from '@totem/components/portfolio/buildings/details/container/BuildingContext';
import SideBarTitle from '@totem/components/ui_components/sideBar/SideBarTitle';
import OrganizationContext from '@totem/components/userContext/organizationContext/organizationContext';
import colors from '@totem/styles/colors';
import addressUtilities from '@totem/utilities/addressUtilities';
import { formatDateOnly, isNotNull } from '@totem/utilities/common';

import EditBuildingModal from './EditBuildingModal';

import './sidebar.css';

const styles = {
  card: {
    width: '100%',
    marginBottom: '3.5rem',
  },
  cardHeader: {
    borderTop: `5px solid`,
    borderImage: `${colors.gradient.accent} 100% 0 0 0`,
  },
  cardBody: {
    padding: '2.4rem',
    paddingBottom: '0',
    borderTop: `1px solid ${colors.antd.borderGray}`,
  },
  nav: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: '1.2rem',
    paddingBottom: '1.6rem',
    borderTop: `1px solid ${colors.antd.borderGray}`,
  },
  navLabel: {
    fontSize: '1.1rem',
    fontWeight: 700,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.06em',
  },
};

const SLIDE_LABELS = ['Building', 'Investment', 'Risk & Profile'];
const DASH = '—';

const BuildingInfo = () => {
  const { data: building } = useContext(BuildingContext);
  const { orgDetails } = useContext(OrganizationContext);
  const [open, setOpen] = useState<boolean>(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const carouselRef = useRef<CarouselRef>(null);

  const address = isNotNull(building)
    ? addressUtilities.getAddress(building.building)
    : '';

  const attrs = isNotNull(building) ? building.building.attributes : null;

  const formatRating = (value: number | undefined) =>
    isNotNull(value) ? `${value} / 10` : DASH;

  const formatPercent = (value: number | undefined) =>
    isNotNull(value) ? `${value}%` : DASH;

  return (
    <>
      <Card
        title={
          <SideBarTitle>
            <div>Building Detail</div>
            <Button
              shape="circle"
              icon={<EditTwoTone twoToneColor={colors.brand.blue} />}
              onClick={() => setOpen(true)}
            />
          </SideBarTitle>
        }
        style={styles.card}
        styles={{ body: styles.cardBody, header: styles.cardHeader }}
      >
        <Carousel
          ref={carouselRef}
          adaptiveHeight
          dots={false}
          arrows={false}
          beforeChange={(_current, next) => setCurrentSlide(next)}
        >
          {/* Slide 1: Building */}
          <div>
            <div styleName="slide-label">Building</div>
            <div styleName="info-row">
              <div styleName="info-label">Building Name</div>
              <div styleName="info-content">
                {isNotNull(building) ? building.building.name : DASH}
              </div>
            </div>
            <div styleName="info-row">
              <div styleName="info-label">Address</div>
              <div styleName="info-content">{address || DASH}</div>
            </div>
            {isNotNull(building) && building.building.type !== '' && (
              <div styleName="info-row">
                <div styleName="info-label">Type</div>
                <div styleName="info-content">{building.building.type}</div>
              </div>
            )}
            {isNotNull(building) && building.building.squareFootage > 0 && (
              <div styleName="info-row">
                <div styleName="info-label">Square Footage</div>
                <div styleName="info-content">
                  {building.building.squareFootage.toLocaleString()}
                </div>
              </div>
            )}
            {isNotNull(building) && isNotNull(building.building.timezone) && (
              <div styleName="info-row">
                <div styleName="info-label">Timezone</div>
                <div styleName="info-content">{building.building.timezone}</div>
              </div>
            )}
          </div>

          {/* Slide 2: Investment */}
          <div>
            <div styleName="slide-label">Investment</div>
            <div styleName="info-row">
              <div styleName="info-label">Fiscal Year End</div>
              <div styleName="info-content">
                {isNotNull(orgDetails) &&
                isNotNull(orgDetails.extras) &&
                orgDetails.extras.fiscalCalendarYearEnd !== ''
                  ? orgDetails.extras.fiscalCalendarYearEnd
                  : DASH}
              </div>
            </div>
            <div styleName="info-row">
              <div styleName="info-label">Investment Posture</div>
              <div styleName="info-content">
                {attrs?.investmentPosture ?? DASH}
              </div>
            </div>
            <div styleName="info-row">
              <div styleName="info-label">Occupancy Rate</div>
              <div styleName="info-content">
                {formatPercent(attrs?.occupancyRate)}
              </div>
            </div>
            <div styleName="info-row">
              <div styleName="info-label">Investment Discount Rate</div>
              <div styleName="info-content">
                {formatPercent(attrs?.investmentDiscountRate)}
              </div>
            </div>
            <div styleName="info-row">
              <div styleName="info-label">Hold Period End Date</div>
              <div styleName="info-content">
                {formatDateOnly(attrs?.holdPeriodEndDate)}
              </div>
            </div>
          </div>

          {/* Slide 3: Risk & Profile */}
          <div>
            <div styleName="slide-label">Risk & Profile</div>
            <div styleName="info-row">
              <div styleName="info-label">Location Risk</div>
              <div styleName="info-content">
                {formatRating(attrs?.locationRisk)}
              </div>
            </div>
            <div styleName="info-row">
              <div styleName="info-label">Tenant Profile</div>
              <div styleName="info-content">
                {formatRating(attrs?.tenantProfile)}
              </div>
            </div>
          </div>
        </Carousel>

        <div style={styles.nav}>
          <Button
            type="text"
            size="small"
            icon={<LeftOutlined />}
            disabled={currentSlide === 0}
            onClick={() => carouselRef.current?.prev()}
          />
          <span style={styles.navLabel}>
            {SLIDE_LABELS[currentSlide]} ({currentSlide + 1} /{' '}
            {SLIDE_LABELS.length})
          </span>
          <Button
            type="text"
            size="small"
            icon={<RightOutlined />}
            disabled={currentSlide === SLIDE_LABELS.length - 1}
            onClick={() => carouselRef.current?.next()}
          />
        </div>
      </Card>
      <EditBuildingModal open={open} setOpen={setOpen} />
    </>
  );
};

export default BuildingInfo;
